import React, { useState, useEffect } from "react";
import vepform from "../../configs/vepform.json"; // Import the JSON
import "../../styles/components/VepForm.css";
import { BsPlay } from "react-icons/bs"; // Add icon import

import {
  useTooltips,
  useCollapsiblePanels,
  createInputChangeHandler,
  FormField,
  useProgressPolling,
  CollapsibleSection,
} from "../shared";
import { ReactSortable } from "react-sortablejs";
import "../../styles/utils/Sortable.css";
const VepForm = ({ parentData, onParentDataChange, token }) => {
  const [formData, setFormData] = useState({});

  // Use shared collapsible panels hook
  const {
    toggleCollapse,
    isCollapsed,
  } = useCollapsiblePanels(vepform.panels, {
    mode: "simple",
    idField: "title",
  });

  // Use shared progress polling hook
  const { isRunning: isGeneratingStats, startProcess: runVep } =
    useProgressPolling("VEP", token);

  // Sortable VEP panels configuration
  const [sortablePanels, setSortablePanels] = useState(() => {
    if (!vepform?.panels) return [];
    
    // Load saved order from localStorage, fallback to default
    const savedOrder = localStorage.getItem('sidebarVepSectionOrder');
    if (savedOrder) {
      try {
        const parsed = JSON.parse(savedOrder);
        // Validate that saved order matches current panels
        if (parsed.length === vepform.panels.length) {
          return parsed;
        }
      } catch {
        // If parsing fails, use default
      }
    }
    
    // Create default order from vepform panels
    return vepform.panels.map((panel, index) => ({
      id: panel.title.replace(/\s+/g, '-').toLowerCase() + '-' + index,
      title: panel.title,
      panel: panel
    }));
  });

  // Handle sortable order change for VEP panels
  const handleVepSortChange = (newOrder) => {
    setSortablePanels(newOrder);
    // Save to localStorage
    try {
      localStorage.setItem('sidebarVepSectionOrder', JSON.stringify(newOrder));
    } catch (error) {
      console.warn('Failed to save VEP section order:', error);
    }
  };

  // Initialize tooltips using shared hook
  useTooltips();

  useEffect(() => {
    // Sync formData with parentData when parentData changes
    setFormData(parentData || {});
  }, [parentData]);

  // Use shared input change handler
  const handleInputChange = createInputChangeHandler(
    formData,
    setFormData,
    onParentDataChange
  );

  // Function to check if a panel has any selected/filled fields
  const isPanelActive = (panel) => {
    if (!panel?.fields || !formData) return false;
    
    return panel.fields.some(field => {
      const value = formData[field.name];
      
      // Check different field types for "active" state
      switch (field.type) {
        case 'checkbox':
          return value === true;
        case 'select':
        case 'text':
        case 'number':
          return value && value !== '' && value !== field.default;
        case 'radio':
          return value && value !== field.default;
        default:
          return value && value !== '' && value !== field.default;
      }
    });
  };

  const renderFields = (panel) => {
    return (
      <div className="vep-fields-grid">
        {panel.fields.map((field, index) => (
          <FormField
            key={index}
            field={field}
            value={formData[field.name]}
            onChange={handleInputChange}
            layout="horizontal"
          />
        ))}
      </div>
    );
  };

  const renderPanels = () => {
    if (!vepform || sortablePanels.length === 0) return null;

    return (
      <ReactSortable
        list={sortablePanels}
        setList={handleVepSortChange}
        animation={150}
        delayOnTouchStart={true}
        delay={2}
        ghostClass="sortable-ghost"
        chosenClass="sortable-chosen"
        dragClass="sortable-drag"
        handle=".card-header"
      >
        {sortablePanels.map((sortablePanel) => {
          const isActive = isPanelActive(sortablePanel.panel);
          return (
            <div key={sortablePanel.id} className="sortable-item">
              <CollapsibleSection
                title={sortablePanel.title}
                isCollapsed={isCollapsed(sortablePanel.title)}
                onToggle={() => toggleCollapse(sortablePanel.title)}
                variant={isActive ? "active" : "default"}
                className={isActive ? "filter-variant-teal" : ""}
              >
                {renderFields(sortablePanel.panel)}
              </CollapsibleSection>
            </div>
          );
        })}
      </ReactSortable>
    );
  };

  return (
    <div className="mb-3">
      {renderPanels()}
      {/* Add button to submit the form with consistent styling */}
      <button
        onClick={() => runVep({ vepConfig: formData })}
        className="btn btn-primary btn-animate w-100 d-flex align-items-center justify-content-center mb-4"
        disabled={isGeneratingStats} // Disable button while stats are generating
      >
        <BsPlay className="me-2" />
        <span className="fw-semibold">Run VEP</span>
      </button>
    </div>
  );
};

export default VepForm;
